import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

function CustomerExperience() {
  const [companyLanguage, setCompanyLanguage] = useState('en');

  useEffect(() => {
    const lang = localStorage.getItem("companyLanguage") || 'en';
    setCompanyLanguage(lang);
    
    const handleStorageChange = () => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      setCompanyLanguage(updatedLang);
    };
    
    window.addEventListener('companyDataUpdated', handleStorageChange);
    
    const interval = setInterval(() => {
      const updatedLang = localStorage.getItem("companyLanguage") || 'en';
      if (updatedLang !== companyLanguage) {
        setCompanyLanguage(updatedLang);
      }
    }, 2000);

    return () => {
      window.removeEventListener('companyDataUpdated', handleStorageChange);
      clearInterval(interval);
    };
  }, [companyLanguage]);

  const isUrdu = companyLanguage === 'ur';

  return (
    <>
      <Helmet>
        <title>{isUrdu ? 'کسٹمر سپورٹ - Galla Mandi Bills & Khata' : 'Customer Experience - Galla Mandi Bills & Khata'}</title>
      </Helmet>
      
      <div
        className={`${isUrdu ? 'font-urdu' : ''} bg-brand-background min-h-screen`}
        style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}
        dir={isUrdu ? 'rtl' : 'ltr'}
      >
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-brand-header via-green-700 to-brand-header text-white py-20 relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute top-0 left-0 w-72 h-72 bg-green-300 rounded-full mix-blend-multiply filter blur-xl animate-blob"></div>
            <div className="absolute top-0 right-0 w-72 h-72 bg-green-400 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-2000"></div>
            <div className="absolute bottom-0 left-1/2 w-72 h-72 bg-green-500 rounded-full mix-blend-multiply filter blur-xl animate-blob animation-delay-4000"></div>
          </div>
          <div className={`container mx-auto px-4 text-center relative z-10 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <h1 className="text-5xl md:text-6xl font-bold mb-4 animate-fade-in-up">
              {isUrdu ? 'کسٹمر سپورٹ' : 'Customer Experience'}
            </h1>
            <p className="text-xl md:text-2xl text-white/90 font-medium">
              {isUrdu 
                ? 'بہترین کسٹمر سروس کے ذریعے اپنے گاہکوں کو متاثر کریں' 
                : 'Delight your customers with exceptional support and service'}
            </p>
          </div>
        </section>

        {/* Features Section */}
        <section className={`py-20 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <h2 className="text-4xl font-bold text-brand-header mb-4">
                {isUrdu ? 'ہماری خدمات' : 'Our Services'}
              </h2>
              <p className="text-gray-600 text-lg">
                {isUrdu ? 'کسٹمر کی اطمینان کے لیے مکمل حل' : 'Complete solutions for customer satisfaction'}
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[
                {
                  icon: "🎯",
                  title: isUrdu ? "فوری ردعمل" : "Quick Response",
                  titleUrdu: "فوری ردعمل",
                  desc: isUrdu ? "24/7 دستیاب کسٹمر سپورٹ" : "24/7 available customer support",
                  descUrdu: "24/7 دستیاب کسٹمر سپورٹ"
                },
                {
                  icon: "💬",
                  title: isUrdu ? "بہترین مواصلات" : "Excellent Communication",
                  titleUrdu: "بہترین مواصلات",
                  desc: isUrdu ? "واضح اور مؤثر بات چیت" : "Clear and effective communication",
                  descUrdu: "واضح اور مؤثر بات چیت"
                },
                {
                  icon: "🔧",
                  title: isUrdu ? "فنی مدد" : "Technical Support",
                  titleUrdu: "فنی مدد",
                  desc: isUrdu ? "ماہرین کی طرف سے فنی مدد" : "Expert technical assistance",
                  descUrdu: "ماہرین کی طرف سے فنی مدد"
                },
                {
                  icon: "📚",
                  title: isUrdu ? "تعلیم و تربیت" : "Training & Education",
                  titleUrdu: "تعلیم و تربیت",
                  desc: isUrdu ? "مکمل گائیڈنس اور تربیت" : "Complete guidance and training",
                  descUrdu: "مکمل گائیڈنس اور تربیت"
                },
                {
                  icon: "✅",
                  title: isUrdu ? "معیار کی ضمانت" : "Quality Assurance",
                  titleUrdu: "معیار کی ضمانت",
                  desc: isUrdu ? "اعلیٰ معیار کی خدمات" : "High quality services",
                  descUrdu: "اعلیٰ معیار کی خدمات"
                },
                {
                  icon: "🚀",
                  title: isUrdu ? "مسلسل بہتری" : "Continuous Improvement",
                  titleUrdu: "مسلسل بہتری",
                  desc: isUrdu ? "مسلسل بہتری اور اپ ڈیٹس" : "Continuous improvement and updates",
                  descUrdu: "مسلسل بہتری اور اپ ڈیٹس"
                }
              ].map((feature, idx) => (
                <div
                  key={idx}
                  className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 group"
                >
                  <div className="text-5xl mb-4 group-hover:scale-110 transition-transform duration-300">
                    {feature.icon}
                  </div>
                  <h3 className={`text-2xl font-bold text-gray-900 mb-3 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? feature.titleUrdu : feature.title}
                  </h3>
                  <p className={`text-gray-600 ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
                    {isUrdu ? feature.descUrdu : feature.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 bg-gradient-to-r from-brand-header to-green-700 text-white">
          <div className={`container mx-auto px-4 text-center ${isUrdu ? 'font-urdu' : ''}`} style={isUrdu ? { fontFamily: "'Noto Nastaliq Urdu', 'Jameel Noori Nastaleeq', 'Al Qalam Taj Nastaleeq', serif" } : {}}>
            <h2 className="text-4xl font-bold mb-4">
              {isUrdu ? 'ہم سے رابطہ کریں' : 'Get In Touch'}
            </h2>
            <p className="text-xl mb-8 text-white/90">
              {isUrdu ? 'بہترین کسٹمر سروس کے لیے آج ہی رابطہ کریں' : 'Contact us today for exceptional customer service'}
            </p>
            <Link
              to="/contact"
              className="bg-brand-button hover:bg-yellow-300 text-gray-900 font-bold px-8 py-4 rounded-xl transition transform hover:scale-105 hover:shadow-lg inline-block"
            >
              {isUrdu ? 'رابطہ کریں' : 'Contact Us'}
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}

export default CustomerExperience;

